import React, { Component } from 'react';
import SwipeableViews from 'react-swipeable-views';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import TitleLine from './components/Core/TitleLine';
import { ThemeContext } from './components/Core/ThemeContext';
import bookConverters from './utilities/bookConversions';
import Outline from './components/Outlines/Outline';
import { LargeGutter } from './components/Core/TextComponents';
import books from './data/books'
import SEO from './components/SEO';
import { buildCanonicalUrl, deriveLegacyCanonicalPath } from './utilities/canonicalUrls';
import { buildBookSeoDescription, buildBookSeoTitle } from './utilities/seo';
import { getPerspectivesForBook } from './data/perspectives';
var bcv_parser = require("bible-passage-reference-parser/js/en_bcv_parser").bcv_parser;
var bcv = new bcv_parser;
import { Capacitor } from '@capacitor/core';
import bookPageSummaries from './data/bookPageSummaries';
const web_version = (Capacitor.getPlatform()==='web');

const querystring = require('querystring');

const bookPageStyles = {
    page: {
        minHeight: '100vh',
        padding: 'clamp(16px, 3vw, 28px)',
        background: 'linear-gradient(180deg, #f8f8f4 0%, #edf4f3 42%, #f9fbfb 100%)',
    },
    frame: {
        width: 'min(100%, 940px)',
        margin: '0 auto',
    },
    stack: {
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
        marginTop: 18,
    },
    hero: {
        display: 'flex',
        background: 'linear-gradient(135deg, #fffdfa 0%, #f4faf9 100%)',
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid rgba(188, 198, 208, 0.4)',
        boxShadow: '0 14px 32px rgba(86, 96, 110, 0.1)',
    },
    heroStrip: {
        width: 18,
        minWidth: 18,
        background: 'var(--color-grad-1)',
    },
    heroBody: {
        flex: 1,
        padding: 'clamp(18px, 3vw, 26px)',
    },
    eyebrow: {
        margin: 0,
        color: 'rgba(90, 105, 108, 0.84)',
        fontSize: '0.74em',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    title: {
        margin: '6px 0 0 0',
        color: 'rgba(0, 0, 0, 0.82)',
        fontFamily: 'Dancing Script, Georgia',
        fontSize: 'clamp(1.9em, 4vw, 2.45em)',
        lineHeight: 1.1,
    },
    subtitle: {
        margin: '10px 0 0 0',
        color: 'rgba(49, 75, 92, 0.74)',
        fontSize: '1em',
        fontWeight: 600,
        lineHeight: 1.5,
    },
    summary: {
        margin: '12px 0 0 0',
        color: 'rgba(0, 0, 0, 0.72)',
        fontSize: '0.98em',
        lineHeight: 1.7,
        maxWidth: 760,
    },
    section: {
        display: 'flex',
        background: '#fff',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 12px 28px rgba(86, 96, 110, 0.08)',
        border: '1px solid rgba(188, 198, 208, 0.38)',
    },
    sectionStrip: {
        width: 12,
        minWidth: 12,
        background: 'var(--color-grad-1)',
    },
    sectionContent: {
        flex: 1,
        minWidth: 0,
        background: 'linear-gradient(180deg, #fffefb 0%, #fcfdfd 100%)',
    },
    sectionButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '16px clamp(16px, 2.6vw, 22px)',
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        textAlign: 'left',
    },
    sectionTitle: {
        margin: 0,
        color: 'rgba(0, 0, 0, 0.8)',
        fontSize: '1.05em',
        fontWeight: 700,
    },
    divider: {
        height: 1,
        background: 'rgba(188, 198, 208, 0.32)',
        margin: '0 clamp(16px, 2.6vw, 22px)',
    },
    sectionBody: {
        padding: '16px clamp(16px, 2.6vw, 22px) 22px',
    },
    collapsedBody: {
        maxHeight: 0,
        overflow: 'hidden',
        paddingTop: 0,
        paddingBottom: 0,
    },
    perspectiveGrid: {
        display: 'grid',
        gap: 12,
    },
    perspectiveLink: {
        display: 'block',
        padding: '14px 16px',
        borderRadius: 12,
        textDecoration: 'none',
        background: 'linear-gradient(135deg, #fffef9 0%, #f5f8fb 100%)',
        border: '1px solid rgba(188, 198, 208, 0.36)',
        boxShadow: '0 8px 22px rgba(86, 96, 110, 0.06)',
    },
    perspectiveEyebrow: {
        margin: 0,
        color: 'rgba(90, 105, 108, 0.84)',
        fontSize: '0.72em',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    perspectiveTitle: {
        margin: '6px 0 0 0',
        color: 'rgba(18, 34, 78, 0.92)',
        fontSize: '1.02em',
        fontWeight: 700,
        lineHeight: 1.35,
    },
    perspectiveSummary: {
        margin: '8px 0 0 0',
        color: 'rgba(0, 0, 0, 0.7)',
        fontSize: '0.95em',
        lineHeight: 1.65,
    },
};

function BookSummary({ subtitle, summary, group, colorIndex }) {
    return (
        <article style={bookPageStyles.hero}>
            <div
                style={{ ...bookPageStyles.heroStrip, background: `var(--color-grad-${colorIndex})` }}
                aria-hidden="true"
            />
            <div style={bookPageStyles.heroBody}>
                <p style={bookPageStyles.eyebrow}>{group || 'Bible book'}</p>
                <p style={bookPageStyles.subtitle}>{subtitle}</p>
                <p style={bookPageStyles.summary}>
                    <strong>AI summary:</strong>{' '}{summary}
                </p>
            </div>
        </article>
    );
}

function BookSection({ children, startsOpen = true, colorIndex }) {
    const [open, setOpen] = React.useState(startsOpen);

    return (
        <section style={bookPageStyles.section}>
            <div
                style={{ ...bookPageStyles.sectionStrip, background: `var(--color-grad-${colorIndex})` }}
                aria-hidden="true"
            />
            <div style={bookPageStyles.sectionContent}>
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    aria-expanded={open}
                    style={bookPageStyles.sectionButton}
                >
                    <span>
                        <span style={bookPageStyles.eyebrow}>Structure</span>
                        <h2 style={bookPageStyles.sectionTitle}>Book outline</h2>
                    </span>
                    <ExpandMoreIcon
                        style={{
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 140ms ease',
                            color: 'rgba(83, 102, 106, 0.85)',
                        }}
                    />
                </button>
                <div style={bookPageStyles.divider} aria-hidden="true" />
                <div
                    aria-hidden={!open}
                    style={{
                        ...bookPageStyles.sectionBody,
                        ...(open ? null : bookPageStyles.collapsedBody),
                    }}
                >
                    {children}
                </div>
            </div>
        </section>
    );
}

function RelatedPerspectives({ topics, colorIndex }) {
    if (!topics.length) return null;

    return (
        <section style={bookPageStyles.section}>
            <div
                style={{ ...bookPageStyles.sectionStrip, background: `var(--color-grad-${colorIndex})` }}
                aria-hidden="true"
            />
            <div style={bookPageStyles.sectionContent}>
                <div style={bookPageStyles.sectionBody}>
                    <span style={bookPageStyles.eyebrow}>Interpretation in conversation</span>
                    <h2 style={bookPageStyles.sectionTitle}>Related perspectives</h2>
                    <div style={{ ...bookPageStyles.perspectiveGrid, marginTop: 14 }}>
                        {topics.map((topic) => (
                            <a key={topic.slug} href={`/perspectives/${topic.slug}`} style={bookPageStyles.perspectiveLink}>
                                <p style={bookPageStyles.perspectiveEyebrow}>{topic.category}</p>
                                <p style={bookPageStyles.perspectiveTitle}>{topic.question}</p>
                                <p style={bookPageStyles.perspectiveSummary}>{topic.seoDescription}</p>
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

class Book extends Component {
    static contextType = ThemeContext;

    static xDown = null;

    static yDown = null;

    bodyScrollLockTargetRef = React.createRef();
    bodyScrollLockTargetElement = null;
    bodyScrollOkayTargetRef = React.createRef();
    bodyScrollOkayTargetElement = null;

    sourcePermissionsRef = React.createRef();

    static getTouches(evt) {
        return evt.touches // browser API
               || evt.originalEvent.touches; // jQuery
    }

    static handleTouchStart(evt) {
        console.log('ts');
        const firstTouch = Book.getTouches(evt)[0];
        Book.xDown = firstTouch.clientX;
        Book.yDown = firstTouch.clientY;
    }

    static handleTouchMove(evt) {
        if (!Book.xDown || !Book.yDown) {
            return;
        }

        const xUp = evt.touches[0].clientX;
        const yUp = evt.touches[0].clientY;

        const xDiff = Book.xDown - xUp;
        const yDiff = Book.yDown - yUp;

        if (Math.abs(xDiff) > Math.abs(yDiff)) { /* most significant */
            if (xDiff > 50) {
                /* left swipe */
                this.incrementPage('');
            } else if (xDiff < -50) {
                /* right swipe */
                this.decrementPage('');
            }
        } else if (yDiff > 0) {
            /* up swipe */
        } else {
            /* down swipe */
        }
        /* reset values */
        Book.xDown = null;
        Book.yDown = null;
    }

    static incrementPage(book) {
        if (web_version)
            window.location.href = `/read/${book}.1/`;
        else
            window.location.href =`/reader?verse=${book}.1`;
    }

    static decrementPage(book) {
        if (web_version)
            window.location.href =`http://${window.location.host}/?noRef=${book}`;
        else
        {
            console.log('going to ' + `/reader?verse=${book}`)
            window.location.href = `/reader?verse=${book}`;
        }
    }

    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    constructor() {
        super();

        // window.scrollTo(0,0)

        window.addEventListener('keydown', (ev) => {
            if (ev.key === 'ArrowRight') Book.incrementPage(ev);
            if (ev.key === 'ArrowLeft') Book.decrementPage(ev);
        });

        window.addEventListener('touchstart', (evt) => { Book.handleTouchStart(evt); }, false);
        window.addEventListener('touchmove', (evt) => { Book.handleTouchMove(evt); }, false);

        this.state = {
            title: '',
            outline: [],
            text: [],
            loading: true,
            sources: {},
            colorIndex: 1,
            book: '',
        };
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
        //enableBodyScroll(this.bodyScrollLockTargetElement);
        //disableBodyScroll(this.bodyScrollOkayTargetElement);
        document.body.style.overflow = "";
    }

    handleResize = () => {
        this.forceUpdate();
    };

    componentDidUpdate() {
        window.scrollTo(0, 0);
    }

    componentDidMount() {
        console.log('REACT_APP_AWS_API', process.env.REACT_APP_AWS_API);

        window.show_footer = false;

        this.bodyScrollLockTargetElement = this.bodyScrollLockTargetRef.current;
        //disableBodyScroll(this.bodyScrollLockTargetElement);
        this.bodyScrollOkayTargetElement = this.bodyScrollOkayTargetRef.current;
        //enableBodyScroll(this.bodyScrollOkayTargetElement);

        document.body.style.overflow = "";

        window.addEventListener('resize', this.handleResize);
        console.log(`ComponentDidMount: ${new Date().getSeconds()} ${window.innerWidth} ${window.scrollY}`);
        const values = querystring.parse(window.location.search.substr(1));
        const pathnameRef = window.location.pathname.replace('/book/','').replace('/book','').replace(/^\//, '');
        const bookRef = values.ref || pathnameRef || values.verse || '';
        const { token } = localStorage;

        if (window.web_version && bookRef) {
            const normalizedPath = deriveLegacyCanonicalPath(window.location.pathname, window.location.search);
            if (`${window.location.pathname}${window.location.search}` !== normalizedPath) {
                window.history.replaceState({}, '', normalizedPath);
            }
        }

        if (!bookRef) {
            this.context.loading(false);
            return;
        }

        this.setState({
            'outline':[...books.booksData.data[bookRef].sections],
            'colorIndex': bookConverters.convertSubGroupToIndex(books.booksData.data[bookRef].Subgroup),
            'title': books.booksData.data[bookRef].Name,
            'loading': false,
            'book': bookRef
        });

        this.context.loading(false);
    }

    
    trimY = this.sourcePermissionsRef?.current?.getBoundingClientRect().y;

    render() {
        //this.trimY = this.sourcePermissionsRef?.current?.getBoundingClientRect().y;
        console.log('Trimy', this.trimY);
        const values = querystring.parse(window.location.search.substr(1));
        const searchedVerse = values.verse || window.location.pathname.replace('/book/','').replace('/book','').replace(/^\//, '');
        const bookData = books.booksData.data[searchedVerse] || books.booksData.data[this.state.book] || null;
        const bookName = this.state.title || bookData?.Name || searchedVerse || 'Bible Book';
        const bookSummary = bookPageSummaries[this.state.book || searchedVerse]
            || bookData?.summary
            || 'This page introduces a book of the Bible and its place in the wider story.';
        const bookSubtitle = bookData?.Summary || 'A book of the Bible';
        const bookGroup = bookData?.Group || bookData?.Subgroup;
        const canonical = buildCanonicalUrl(`/book/${this.state.book || searchedVerse}`);
        const seoTitle = buildBookSeoTitle({
            name: bookName,
            summary: bookData?.summary || bookData?.Summary || '',
        });
        const seoDescription = buildBookSeoDescription({
            name: bookName,
            summary: bookData?.summary || bookData?.Summary || '',
            author: bookData?.Author || '',
            concerns: bookData?.Concerns || '',
        });
        const relatedPerspectives = getPerspectivesForBook(this.state.book || searchedVerse);
        document.title = seoTitle;
        const aggregatedSources = this.state.sources;
        console.log(`Sources: ${new Date().getSeconds()}`, aggregatedSources);
        return (
            <>
            <SEO
                canonical={canonical}
                title={seoTitle}
                description={seoDescription}
                type="article"
            />
                <SwipeableViews ref={this.bodyScrollLockTargetRef}>
                    <div>
                        <LargeGutter />
                        <TitleLine
                            title={bookName}
                            book={this.state.book}
                            visible={!this.state.loading}
                            colorIndex={this.state.colorIndex}
                            onclickBack={() => Book.decrementPage(this.state.book)}
                            onclickForward={() => Book.incrementPage(this.state.book)}
                            hideBot={true}
                            hideArrows={!web_version}
                        />
                        <div style={bookPageStyles.page}>
                            <div style={bookPageStyles.frame}>
                                <BookSummary
                                    subtitle={bookSubtitle}
                                    summary={bookSummary}
                                    group={bookGroup}
                                    colorIndex={this.state.colorIndex}
                                />
                                <div style={bookPageStyles.stack}>
                                    <RelatedPerspectives topics={relatedPerspectives} colorIndex={this.state.colorIndex} />
                                    <BookSection colorIndex={this.state.colorIndex}>
                                        <div ref={this.bodyScrollOkayTargetRef}>
                                            <Outline outline={this.state.outline} colorIndex={this.state.colorIndex} />
                                        </div>
                                    </BookSection>
                                </div>
                            </div>
                        </div>
                    </div>
            </SwipeableViews>
            </>
        );
    }
}

export default Book;
